import { useEffect, useRef } from "react";
import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Lenis from "lenis";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AuthCallback from "@/components/AuthCallback";
import Seo from "@/components/Seo";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Tarot from "@/pages/Tarot";
import Admin from "@/pages/Admin";
import { Toaster } from "@/components/ui/sonner";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-ink text-paper flex items-center justify-center">
          <p className="font-display text-2xl text-mist">Something flickered out. Refresh to return.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

const ROUTE_META = {
  "/": {
    title: "moonspiritvida lifestyle — Crystals, Tarot & the Handmade Mystic",
    description: "A mystic's apothecary and reading room. Crystals, rare minerals, tarot decks, sage, crochet, homemade beauty and jewelry — plus tarot readings by the keeper.",
    canonical: "https://moonspiritvida.com/",
  },
  "/shop": {
    title: "Shop Crystals, Tarot Decks & Handmade Goods — moonspiritvida",
    description: "Browse the full cabinet: crystal tumbles, clusters, bracelets and lockets by stone, tarot decks, sage, crochet, soaps and silver jewelry.",
    canonical: "https://moonspiritvida.com/shop",
  },
  "/tarot": {
    title: "Book a Tarot Reading — moonspiritvida",
    description: "Ask the question that's been circling. Single card pulls, three-card stories and Celtic cross readings — in person or over a call.",
    canonical: "https://moonspiritvida.com/tarot",
  },
  "/admin": {
    title: "Keeper's Desk — moonspiritvida",
    noindex: true,
  },
};

function RouteSeo() {
  const { pathname } = useLocation();
  const meta = ROUTE_META[pathname];
  if (!meta) return null;
  return <Seo {...meta} />;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash?.includes("session_id=")) return;
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function Shell() {
  const { pathname, hash } = useLocation();
  if (hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <div className="grain min-h-screen bg-ink text-paper">
      <RouteSeo />
      <Header />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/product/:id" element={<ProductDetail />} />
        <Route path="/tarot" element={<Tarot />} />
        <Route path="/admin" element={<Admin />} />
      </Routes>
      {pathname !== "/admin" && <Footer />}
    </div>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Shell />
          <Toaster theme="dark" position="top-right" richColors />
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
